import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { format, isSameDay } from "date-fns";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonItem,
  IonAvatar,
  IonList,
  IonTextarea,
} from "@ionic/react";
import { paperPlaneOutline } from "ionicons/icons";
import "../../theme/AppStyles.css";
import { ProfileContext } from "../../context/ProfileContext";
import { WerkerJobContext } from "../../context/JobContextWerker";
import { SchedJobContext } from "../../context/JobContextScheduler";
import { parseNotes } from "../../functions/FX_ParseAndMap";
import { sendChatMessage } from "../../utils/chatUtils";
import { useMessageRead } from "../../hooks/useMessageRead";
import { useChatMessages } from "../../hooks/useChatMessages";
import { useChatType } from "../../hooks/useChatType";
import { ChatData, ThisChatData } from "../../types/chat";

interface ChatPanelProps {
  chatId: string | null;
  hideHeader?: boolean;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ chatId, hideHeader = false }) => {
  const { profile, myChats, myAssociates } = useContext(ProfileContext);
  const [sendMessage, setSendMessage] = useState("");
  const listRef = useRef<HTMLIonListElement>(null);
  const textareaRef = useRef<HTMLIonTextareaElement | null>(null);
  const { werkerJob } = useContext(WerkerJobContext);
  const { schedJob } = useContext(SchedJobContext);
  const [isSending, setIsSending] = useState(false);
  const { markChatAsRead } = useMessageRead();
  const lastMarkedChatRef = useRef<string>("");

  // Use shared chat messages hook
  const {
    chatType,
    renderMessages,
    setPendingMessages,
  } = useChatMessages(chatId);

  const [thisChat, setThisChat] = useState<ThisChatData>({
    DMuid: "",
    IsRead: true,
    MessageDate: "",
    MessagePreview: "",
    MessageTitle: "",
    SendersPic: "../assets/Briefcase.png",
    Key: "",
  });

  // Use shared chat type hook
  const resolvedChatType = useChatType(chatType, thisChat);

  const makeClientId = () => `${profile.UserId}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const postMessage = async () => {
    if (!chatId) return;
    const text = (sendMessage || "").trim();
    if (!text) return;

    const clientId = makeClientId();
    const now = Date.now();
    const optimistic: ChatData = {
      id: clientId,
      clientId,
      pending: true,
      failed: false,

      IsRead: true,
      MessageAuthorId: profile.UserId,
      MessageAuthor: `${profile.FirstName} ${profile.LastName}`,
      MessageText: text,
      JobDocId: chatId,
      UserActionTakenId: 0,
      UserActionTakenUserId: 0,
      createdAt: now,
      updatedAt: now,
      MessageAuthorPic: profile.ProfilePicURL,
      NewChatPath: "",
    };
    setPendingMessages((prev) => [...prev, optimistic]);
    setSendMessage("");
    setIsSending(true);
    try {
      await sendChatMessage(
        chatId,
        text,
        resolvedChatType,
        profile,
        myChats,
        myAssociates,
        schedJob,
        werkerJob,
        clientId
      );
    } catch (error) {
      console.error("Error posting message:", error);
      setPendingMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m))
      );
    } finally {
      setIsSending(false);
    }
  };

  const updateMsgIsRead = useCallback(async () => {
    if (!chatId) return;
    const chatExists = myChats.find((chat: { Key: string }) => chat.Key === chatId);
    if (chatExists) {
      await markChatAsRead(chatId, resolvedChatType);
    }
  }, [chatId, myChats, resolvedChatType, markChatAsRead]);

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      const lastItem = listRef.current.lastElementChild;
      if (lastItem) {
        lastItem.scrollIntoView({ behavior: "auto", block: "end" });
      }
    }
  }, []);

  // Get this chat details
  useEffect(() => {
    if (!chatId) {
      setThisChat({
        DMuid: "",
        IsRead: true,
        MessageDate: "",
        MessagePreview: "",
        MessageTitle: "",
        SendersPic: "../assets/Briefcase.png",
        Key: "",
      });
      return;
    }

    const thisMyChat = myChats.find((chat: any) => chat.Key === chatId);
    if (thisMyChat) {
      setThisChat(thisMyChat);
    }
  }, [chatId, myChats]);

  // Scroll to bottom when messages change
  const lastRenderMessageId = useMemo(() => {
    if (renderMessages.length === 0) return null;
    return renderMessages[renderMessages.length - 1]?.id;
  }, [renderMessages]);

  useEffect(() => {
    if (lastRenderMessageId) {
      setTimeout(() => scrollToBottom(), 100);
      const key = `${chatId}_${resolvedChatType}`;
      if (resolvedChatType && lastMarkedChatRef.current !== key) {
        lastMarkedChatRef.current = key;
        updateMsgIsRead();
      }
    }
  }, [lastRenderMessageId, chatId, resolvedChatType, scrollToBottom, updateMsgIsRead]);

  if (!chatId) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: 12,
          opacity: 0.85,
        }}
      >
        <img
          src="../assets/GorillaJonesNoBackground.png"
          alt="WerkApe"
          style={{ width: "70%", maxWidth: 480, height: "auto", objectFit: "contain" }}
        />
        <p style={{ color: "var(--ion-color-medium)", fontSize: 14, margin: 0 }}>
          Select a conversation
        </p>
      </div>
    );
  }

  const isScheduler = profile.UserLastRoleScheduler === true;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      {!hideHeader && <div
        style={{
          padding: "16px",
          borderBottom: "1px solid var(--ion-color-light-shade)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          backgroundColor: "var(--ion-color-light)",
        }}
      >
        <IonAvatar
          style={{ width: "40px", height: "40px" }}
        >
          <img
            src={thisChat.SendersPic || "../assets/profilePic.png"}
            alt=""
            onError={(e: any) => {
              e.target.src = "../assets/profilePic.png";
            }}
          />
        </IonAvatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>{thisChat.MessageTitle || "Chat"}</h3>
        </div>
      </div>}

      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          backgroundColor: "var(--ion-background-color)",
        }}
      >
        {renderMessages.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--ion-color-medium)" }}>
            <h4>Start chatting ...</h4>
          </div>
        )}

        {renderMessages.length > 0 && (
          <IonList
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              padding: "0px",
              backgroundColor: "transparent",
            }}
            ref={listRef}
          >
            {renderMessages.map((message, index, arr) => {
              const prevMessage = arr[index - 1];
              const isNewDay =
                !prevMessage || !isSameDay(new Date(message.createdAt), new Date(prevMessage.createdAt));

              return (
                <React.Fragment key={message.id}>
                  {isNewDay && (
                    <div
                      style={{
                        textAlign: "center",
                        margin: "10px 0",
                        marginTop: "25px",
                        color: "#92949c",
                        fontWeight: "bold",
                      }}
                    >
                      {format(new Date(message.createdAt), "MMMM d, yyyy")}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      flexDirection: message.MessageAuthorId === profile.UserId ? "row-reverse" : "row",
                      alignItems: "flex-end",
                      gap: "10px",
                      margin: "12px",
                    }}
                  >
                    {message.MessageAuthorId !== profile.UserId && (
                      <IonAvatar
                        style={{
                          marginLeft: "-12px",
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      >
                        <img src={message.MessageAuthorPic} alt="" />
                      </IonAvatar>
                    )}

                    <div
                      style={{
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        position: "relative",
                        alignSelf: message.MessageAuthorId === profile.UserId ? "flex-end" : "flex-start",
                        backgroundColor:
                          message.MessageAuthorId === profile.UserId
                            ? isScheduler
                              ? "#E0742E"
                              : "#ffcc4d"
                            : "#e8e8e8",
                        color: message.MessageAuthorId === profile.UserId ? "#000000" : "#1e1e1e",
                        maxWidth: message.MessageAuthorId === profile.UserId ? "80%" : "75%",
                        padding: "10px 15px",
                        border: "1px solid",
                        borderRadius: "10px",
                        borderBottomRightRadius: message.MessageAuthorId === profile.UserId ? "0px" : "10px",
                        borderBottomLeftRadius: message.MessageAuthorId === profile.UserId ? "10px" : "0px",
                        minWidth: "55px",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#6d6e73" }}>
                        {message.MessageAuthorId !== profile.UserId && <span>{message.MessageAuthor}</span>}
                      </span>
                      <p
                        style={{
                          margin: 0,
                          userSelect: "text",
                          color: message.MessageAuthorId === profile.UserId ? "black" : "",
                        }}
                      >
                        {parseNotes(message.MessageText)}
                      </p>
                      <span
                        style={{
                          fontSize: "12px",
                          minWidth: "55px",
                          color: "#92949c",
                          position: "absolute",
                          bottom: "-18px",
                          right: "0",
                        }}
                      >
                        {message.pending ? "Sending..." : format(new Date(message.createdAt), "h:mm a")}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <br />
          </IonList>
        )}
      </div>

      {/* Message Input */}
      <div
        style={{
          padding: "16px",
          borderTop: "1px solid var(--ion-color-light-shade)",
          backgroundColor: "var(--ion-color-light)",
        }}
      >
        <IonGrid 
          style={{ padding: 0 }}
        >
          <IonRow
          >
            <IonCol 
              size="10"
            >
              <IonItem 
                counter={true} 
                lines="none"
              >
                <IonTextarea
                  ref={textareaRef}
                  spellCheck={true}
                  autoCapitalize="sentences"
                  autoGrow={true}
                  rows={1}
                  maxlength={1000}
                  enterkeyhint="send"
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void postMessage();
                    }
                  }}
                  style={{
                    width: "100%",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                  value={sendMessage}
                  placeholder="Send Message..."
                  onIonChange={(e) => setSendMessage(e.detail.value!)}
                />
              </IonItem>
            </IonCol>

            <IonCol 
              size="2"
            >
              <IonButton
                type="button"
                fill="clear"
                disabled={isSending}
                onPointerDown={(e) => {
                  e.preventDefault();
                }}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => void postMessage()}
              >
                <IonIcon 
                  size="small" 
                  icon={paperPlaneOutline}
                />
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
    </div>
  );
};
